import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const ContentContainer = styled.div<{ $isLoaded: boolean }>`
  width: 100%;
  min-height: ${props => props.$isLoaded ? 'auto' : '100px'}; /* Hauteur minimale pour permettre l'observer */
  opacity: ${props => props.$isLoaded ? '1' : '0'};
  transition: opacity 0.3s ease-in;
  will-change: opacity;
  pointer-events: ${props => props.$isLoaded ? 'auto' : 'none'};
`;

const LoadingPlaceholder = styled.div`
  width: 100%;
  height: 0; /* Pas de hauteur pour le placeholder */
  background: transparent;
`;

interface LazyPageContentProps {
  children: React.ReactNode;
  loadImmediately?: boolean;
}

const LazyPageContent: React.FC<LazyPageContentProps> = ({ 
  children, 
  loadImmediately = false 
}) => {
  const [isLoaded, setIsLoaded] = useState(loadImmediately);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (loadImmediately || isLoaded) {
      return;
    }

    const loadContent = () => {
      setIsLoaded(true);
    };

    // Utiliser IntersectionObserver pour charger quand visible
    if ('IntersectionObserver' in window && containerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadContent();
              if (observerRef.current) {
                observerRef.current.disconnect();
              }
            }
          });
        },
        { 
          rootMargin: '300px', // Commencer à charger 300px avant d'être visible
          threshold: 0.01
        }
      );

      observerRef.current.observe(containerRef.current);
    } else {
      // Fallback pour navigateurs sans IntersectionObserver
      setTimeout(loadContent, 100);
    }

    // Timeout de sécurité pour charger même si l'observer ne se déclenche pas
    // Réduit à 100ms pour la page d'accueil qui doit être visible immédiatement
    const timeoutId = setTimeout(loadContent, 100);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      clearTimeout(timeoutId);
    };
  }, [loadImmediately, isLoaded]);

  return (
    <ContentContainer ref={containerRef} $isLoaded={isLoaded}>
      {isLoaded ? children : <LoadingPlaceholder />}
    </ContentContainer>
  );
};

export default LazyPageContent;

